import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { FileUploadService } from '../api-services/file-upload.service';
import { DroneResource } from '../data-models/drone-resource.model';
import { DroneResourceRequest } from '../request-models/drone-resource-request.model';
import { DroneResourceUpload } from '../request-models/drone-resource-upload.model';
import { DroneResourceUploadUrlRequest } from '../request-models/drone-resource-upload-url-request.model';
import { DroneResourceUploadTarget } from '../response-models/drone-resource-upload-target.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * A drone's documents: `/admin/drones/:droneId/resources[/:id[/download-url]]` and `/upload-url`
 * (hub `admin-drone-resources-controller.ts`), a nested endpoint under the drone.
 *
 * Every method rejects with an `ApiError` (or an `Error` for a file that fails local validation or a step
 * that answers without data), so callers have a single error path.
 */
@Injectable({
  providedIn: 'root'
})
export class DroneResourceService extends BaseService<DroneResource> {
  private readonly fileUploadService = inject(FileUploadService);

  constructor() {
    super('resources', DroneResource);
  }

  /** GET the drone's resources. `adminOnly` is `'true'` / `'false'`, or blank for both. */
  getResourcesByDroneId(droneId: string, resourceType?: string, adminOnly?: string): Promise<DroneResource[]> {
    const query = { resourceType: resourceType || undefined, adminOnly: toAdminOnlyFilter(adminOnly) };
    return firstValueFrom(this.getMany({ prefix: dronePrefix(droneId), query }));
  }

  /**
   * Attach a file to a drone: pre-sign, PUT the bytes to S3, then register the row with the s3Key and cdnUrl
   * from the pre-sign. A failure at the last step leaves an orphaned S3 object, the same trade-off the other
   * attachment flows accept.
   */
  async uploadResourceByDroneId(droneId: string, file: File, meta: DroneResourceUpload): Promise<DroneResource> {
    const fileFields = await this.uploadFile(droneId, file);
    const registered = await firstValueFrom(this.create({ ...toMetaFields(meta), ...fileFields }, { prefix: dronePrefix(droneId) }));
    if (!registered) {
      throw new Error('Upload saved to storage but could not be recorded.');
    }
    return registered;
  }

  /** PATCH a resource's metadata. Pass a file to replace the document too (the same pre-sign and PUT first). */
  async updateResource(droneId: string, resourceId: string, meta: DroneResourceUpload, file?: File | null): Promise<DroneResource> {
    const body: DroneResourceRequest = file ? { ...toMetaFields(meta), ...(await this.uploadFile(droneId, file)) } : toMetaFields(meta);
    const opts = { prefix: dronePrefix(droneId), id: resourceId };
    const updated = await firstValueFrom(this.request('PATCH', opts, toResourceOrNull, body));
    if (!updated) {
      throw new Error('Could not save your changes.');
    }
    return updated;
  }

  /**
   * Mint a short-lived signed GET for a resource. Resources are stored private, so `cdnUrl` is not directly
   * loadable. The URL expires in an hour: fetch it at click time rather than caching it.
   */
  async getResourceDownloadUrl(droneId: string, resourceId: string): Promise<string> {
    const opts = { prefix: dronePrefix(droneId), id: resourceId, path: 'download-url' };
    const url = await firstValueFrom(this.request('GET', opts, data => (isRecord(data) ? nullableString(data['downloadUrl']) : null)));
    if (!url) {
      throw new Error('Could not open this resource.');
    }
    return url;
  }

  /** Soft-delete a resource. The S3 object is intentionally left in place. */
  async deleteResource(droneId: string, resourceId: string): Promise<void> {
    await firstValueFrom(this.delete(resourceId, { prefix: dronePrefix(droneId) }));
  }

  /** Validates the file, pre-signs, PUTs it to S3 and returns the file fields for the register / PATCH body. */
  private async uploadFile(droneId: string, file: File): Promise<Required<Pick<DroneResourceRequest, FileField>>> {
    const validationError = this.fileUploadService.validate(file);
    if (validationError) {
      throw new Error(validationError);
    }
    const target = await this.getUploadTarget(droneId, file);
    // No x-amz-acl: the hub signs drone resources private.
    await this.fileUploadService.uploadToSignedUrl(target.uploadUrl, file);
    return {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileExtension: this.fileUploadService.getFileExtension(file.name),
      s3Key: target.s3Key,
      cdnUrl: target.cdnUrl,
    };
  }

  private async getUploadTarget(droneId: string, file: File): Promise<DroneResourceUploadTarget> {
    const body: DroneResourceUploadUrlRequest = { filename: file.name, contentType: file.type || 'application/octet-stream' };
    const target = await firstValueFrom(this.request('POST', { prefix: dronePrefix(droneId), path: 'upload-url' }, toUploadTarget, body));
    if (!target) {
      throw new Error('Could not prepare the upload.');
    }
    return target;
  }
}

type FileField = 'fileName' | 'fileSize' | 'fileType' | 'fileExtension' | 's3Key' | 'cdnUrl';

/** `admin/drones/{droneId}`, with the id as its own segment. */
function dronePrefix(droneId: string): string[] {
  return ['admin', 'drones', droneId];
}

function toAdminOnlyFilter(adminOnly: string | undefined): boolean | undefined {
  return adminOnly === 'true' ? true : adminOnly === 'false' ? false : undefined;
}

function toMetaFields(meta: DroneResourceUpload): DroneResourceRequest {
  return { resourceType: meta.resourceType, adminOnly: meta.adminOnly, caption: meta.caption ?? null };
}

function toResourceOrNull(data: unknown): DroneResource | null {
  return data === null || data === undefined ? null : new DroneResource(data);
}

function toUploadTarget(data: unknown): DroneResourceUploadTarget | null {
  const source = isRecord(data) ? data : {};
  const uploadUrl = nullableString(source['uploadUrl']);
  const s3Key = nullableString(source['s3Key']);
  const cdnUrl = nullableString(source['cdnUrl']);
  return uploadUrl && s3Key && cdnUrl ? { uploadUrl, s3Key, cdnUrl } : null;
}
